import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { compareInterviews } from "../../../api/interview";
import RecommendationBadge from "../../../components/shared/RecommendationBadge";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";
import CandidateCardsRow from "./CandidateCardsRow";
import HeatmapGrid from "./HeatmapGrid";
import QuestionScoresTable from "./QuestionScoresTable";
import AnswerSidePanel from "./AnswerSidePanel";
import CandidatePicker from "./CandidatePicker";
import { downloadComparePdf } from "../../../utils/compareToPdf";

// ── /admin/compare — side-by-side candidate comparison ──────────────────────
// Usage: /admin/compare?ids=intv_001,intv_002,intv_003&jobId=10
// ids = interview IDs from the picker. Hits POST {INTERVIEW_API_BASE_URL}/api/v1/interviews/compare
// with { tenant_id, interview_ids }. Graceful error state on failure.

const BAND_COLORS = {
  strong:   "text-emerald-700 bg-emerald-50",
  moderate: "text-amber-700 bg-amber-50",
  weak:     "text-red-700 bg-red-50",
  unknown:  "text-slate-500 bg-slate-50",
};

const scoreColor = (v) => {
  if (v == null) return "text-slate-400";
  if (v >= 8) return "text-emerald-600 font-semibold";
  if (v >= 6) return "text-amber-600";
  return "text-red-600";
};

const fmt = (v, digits = 1) => (v == null ? "—" : (+v).toFixed(digits));
const pct = (v) => (v == null ? "—" : `${Math.round(+v)}%`);

export default function Compare() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  // `ids` are interview IDs (strings — e.g. "intv_smoke_001"). The legacy
  // numeric-candidateId contract is gone now that the picker uses the
  // interview list as its source of truth.
  const interviewIds = useMemo(
    () => (params.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean),
    [params]
  );
  const jobId = Number(params.get("jobId")) || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [answerPanel, setAnswerPanel] = useState({ open: false, candidate: null, question: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [advanceState, setAdvanceState] = useState({ pending: false, result: null, error: null });

  const advanceTopCandidate = async () => {
    if (!data?.candidates || data.candidates.length === 0) return;
    if (!jobId) {
      setAdvanceState({ pending: false, result: null, error: "Missing jobId — can't shortlist without it" });
      return;
    }
    const top = [...data.candidates]
      .filter((c) => c.overallScore != null)
      .sort((a, b) => Number(b.overallScore) - Number(a.overallScore))[0];
    if (!top) {
      setAdvanceState({ pending: false, result: null, error: "No scored candidate to advance" });
      return;
    }
    setAdvanceState({ pending: true, result: null, error: null });
    try {
      await axiosInstance.post(`/job-posting/pipeline/${top.userId}/${jobId}/shortlist`);
      setAdvanceState({ pending: false, result: top, error: null });

      // Best-effort SHORTLISTED email after the pipeline transition.
      // Skips silently when no email is on the candidate row.
      if (top.primaryEmail) {
        try {
          await axiosInstance.post(`/job-posting/stage-emails/send`, {
            stageTo: "SHORTLISTED",
            candidateId: top.userId,
            jobId,
            recipientEmail: top.primaryEmail,
            variables: {
              candidateName: top.fullName || top.primaryEmail,
              jobTitle: `Job #${jobId}`,
              companyName: "Ample Hire",
              recruiterName: "the hiring team",
              nextStepDescription: "We'll be in touch shortly with next steps.",
            },
          });
        } catch { /* status flipped — surfacing this in the compare advance
                     toast would noise up a happy-path action; ignore */ }
      }
    } catch (e) {
      setAdvanceState({ pending: false, result: null, error: e?.message || "Failed" });
    }
  };

  const applyPickerSelection = ({ ids, jobId: pickedJobId }) => {
    const qs = new URLSearchParams();
    qs.set("ids", ids.join(","));
    if (pickedJobId) qs.set("jobId", String(pickedJobId));
    nav(`/admin/compare?${qs.toString()}`);
    setPickerOpen(false);
  };

  useEffect(() => {
    document.title = "Compare - Ample Hire";
  }, []);

  useEffect(() => {
    if (interviewIds.length < 2) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const tenantId = localStorage.getItem("tenantId");
        const r = await compareInterviews(tenantId, interviewIds);
        setData(r?.data || null);
      } catch (e) {
        setError(e?.message || e?.data?.error || "Failed to load comparison");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [interviewIds.join(",")]);

  // ── Empty state when user lands with no ids ─────────────────────────────
  if (interviewIds.length < 2) {
    return (
      <div className="page-enter">
        <Header nav={nav} />
        <CandidatePicker
          initialIds={interviewIds}
          onConfirm={applyPickerSelection}
        />
      </div>
    );
  }

  // ── Loading / error states ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-enter">
        <Header nav={nav} />
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <LoadingState variant="skeleton" shape="card" count={3} />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page-enter">
        <Header nav={nav} />
        <ErrorState
          status={error?.status || 500}
          title="Couldn't load the comparison"
          description={typeof error === "string" ? error : undefined}
          retry={() => window.location.reload()}
        />
      </div>
    );
  }
  if (!data) return null;

  const candidates = data.candidates || [];
  const metricRows = [
    { section: "OVERALL", rows: [
      { key: "overallScore",  label: "Overall score",   format: (v) => fmt(v, 1), highlight: "max" },
      { key: "avgVideoScore", label: "Avg video score", format: (v) => fmt(v, 1), highlight: "max" },
      { key: "audioScore",    label: "Audio score",     format: (v) => fmt(v, 1), highlight: "max" },
    ]},
    { section: "INTEGRITY", rows: [
      { key: "integrityScore",         label: "Integrity score",    format: (v) => fmt(v, 1), highlight: "max" },
      { key: "riskLevel",              label: "Risk level",         format: (v) => v || "—",
        flag: (v) => v === "HIGH" },
      { key: "maxCheatingProbability", label: "Max cheat prob",     format: (v) => pct(v),
        flag: (v) => v != null && v >= 50 },
      { key: "tabSwitchCount",         label: "Tab switches",       format: (v) => v ?? "—",
        flag: (v) => v != null && v >= 5 },
      { key: "multipleFaceCount",      label: "Multiple faces",     format: (v) => v ?? "—",
        flag: (v) => v != null && v >= 1 },
      { key: "gazeOffScreenCount",     label: "Gaze off-screen",    format: (v) => v ?? "—" },
    ]},
    { section: "AUDIO / SPEECH", rows: [
      { key: "audioFluency",   label: "Fluency",         format: (v) => fmt(v, 2) },
      { key: "speakingSpeed",  label: "Speed (wpm)",     format: (v) => fmt(v, 0) },
      { key: "pausesPer15Sec", label: "Pauses per 15s",  format: (v) => fmt(v, 1) },
      { key: "voicePitch",     label: "Voice pitch (Hz)",format: (v) => fmt(v, 0) },
    ]},
  ];

  return (
    <div className="page-enter">
      <Header
        nav={nav}
        positionName={data.positionName}
        count={candidates.length}
        onEditSelection={() => setPickerOpen(true)}
      />

      {pickerOpen && (
        <CandidatePicker
          asModal
          initialIds={interviewIds}
          onConfirm={applyPickerSelection}
          onCancel={() => setPickerOpen(false)}
        />
      )}

      <CandidateCardsRow candidates={candidates} jobId={jobId} />

      <HeatmapGrid candidates={candidates} />

      <QuestionScoresTable
        candidates={candidates}
        questionScores={data.questionScores}
        onCellClick={({ candidate, question }) =>
          setAnswerPanel({ open: true, candidate, question })
        }
      />

      <AnswerSidePanel
        open={answerPanel.open}
        candidate={answerPanel.candidate}
        question={answerPanel.question}
        onClose={() => setAnswerPanel({ open: false, candidate: null, question: null })}
      />

      {/* Scores table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left p-3 text-xs text-vf-muted uppercase">Metric</th>
              {candidates.map((c) => (
                <th key={c.userId} className="text-left p-3 min-w-[180px]">
                  <div className="font-semibold text-vf-base">{c.fullName}</div>
                  <div className="text-[11px] text-vf-muted truncate">{c.primaryEmail}</div>
                  <div className="mt-1">
                    <RecommendationBadge score={c.overallScore} size="sm" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricRows.map((section) => (
              <React.Fragment key={section.section}>
                <tr className="bg-slate-50">
                  <td colSpan={candidates.length + 1} className="px-3 py-1.5 text-[11px] text-vf-muted uppercase font-semibold tracking-wide">
                    {section.section}
                  </td>
                </tr>
                {section.rows.map((row) => {
                  const vals = candidates.map((c) => c[row.key]);
                  const numericMax = Math.max(...vals.filter((v) => typeof v === "number"));
                  return (
                    <tr key={row.key} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs text-vf-muted">{row.label}</td>
                      {candidates.map((c) => {
                        const v = c[row.key];
                        const isMax = row.highlight === "max" && typeof v === "number" && v === numericMax;
                        const flagged = row.flag && row.flag(v);
                        return (
                          <td key={c.userId} className={`px-3 py-2 text-sm ${typeof v === "number" ? scoreColor(v) : ""}`}>
                            {isMax && <span title="Top">⭐ </span>}
                            {flagged && <span title="Flag">🚩 </span>}
                            {row.format(v)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}

            {/* Competencies row — one cell per candidate showing 3 bands */}
            <tr className="bg-slate-50">
              <td colSpan={candidates.length + 1} className="px-3 py-1.5 text-[11px] text-vf-muted uppercase font-semibold tracking-wide">
                COMPETENCIES
              </td>
            </tr>
            <tr className="border-t border-slate-100 align-top">
              <td className="px-3 py-3 text-xs text-vf-muted">Skills breakdown</td>
              {candidates.map((c) => (
                <td key={c.userId} className="px-3 py-3">
                  <div className="flex flex-col gap-1">
                    {(c.competencies || []).map((k) => (
                      <div key={k.skillName} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-vf-muted truncate">{k.skillName}</span>
                        <span className={`px-1.5 py-0.5 rounded font-medium ${BAND_COLORS[k.band] || BAND_COLORS.unknown}`}>
                          {fmt(k.score, 1)}
                        </span>
                      </div>
                    ))}
                    {(c.competencies || []).length === 0 && <div className="text-xs text-slate-400">No competency data</div>}
                  </div>
                </td>
              ))}
            </tr>

            {/* AI summary row */}
            <tr className="bg-slate-50">
              <td colSpan={candidates.length + 1} className="px-3 py-1.5 text-[11px] text-vf-muted uppercase font-semibold tracking-wide">
                AI SUMMARY
              </td>
            </tr>
            <tr className="border-t border-slate-100 align-top">
              <td className="px-3 py-3 text-xs text-vf-muted">Narrative</td>
              {candidates.map((c) => (
                <td key={c.userId} className="px-3 py-3 text-xs leading-relaxed text-vf-base whitespace-pre-line">
                  {c.aiSummary || <span className="text-slate-400">Not yet scored</span>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Recommendation block */}
      <div className="rounded-xl border border-vf-highlight bg-vf-tint/60 p-5 mb-5">
        <h2 className="text-sm font-semibold text-vf-base mb-1">AI Recommendation</h2>
        <p className="text-sm leading-relaxed text-vf-base whitespace-pre-line">{data.aiRecommendation}</p>
        {data.suggestedOrdering && (
          <div className="mt-3 text-xs text-vf-muted">
            <b>Suggested order:</b> {data.suggestedOrdering}
          </div>
        )}
        {data.flaggedCandidates && data.flaggedCandidates.length > 0 && (
          <div className="mt-2 text-xs text-red-700">
            🚩 <b>Flagged:</b> {data.flaggedCandidates.join(", ")}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 justify-between">
        <div className="text-xs text-vf-muted">
          Data source: <b>{data.dataSource || "—"}</b> · generated {data.generatedAt ? new Date(data.generatedAt).toLocaleTimeString() : ""}
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav(-1)} className="px-3 py-1.5 rounded border border-slate-300 text-xs">← Back</button>
          <button
            onClick={() => downloadComparePdf(data)}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Export PDF
          </button>
          {(() => {
            const topByScore = [...candidates]
              .filter((c) => c.overallScore != null)
              .sort((a, b) => Number(b.overallScore) - Number(a.overallScore))[0];
            const topAlready = topByScore && ["SHORTLISTED", "OFFERED", "HIRED"].includes(topByScore.dispositionStatus);
            const displayed = advanceState.result || (topAlready ? topByScore : null);
            return (
              <button
                onClick={advanceTopCandidate}
                disabled={advanceState.pending || !!displayed}
                className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {advanceState.pending
                  ? "Shortlisting…"
                  : displayed
                  ? `✓ Shortlisted ${displayed.fullName}`
                  : "Advance top candidate"}
              </button>
            );
          })()}
        </div>
      </div>
      {advanceState.error && (
        <div className="mt-2 text-xs text-red-700 text-right">{advanceState.error}</div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────
const Header = ({ nav, positionName, count, onEditSelection }) => (
  <div className="flex items-end justify-between mb-6">
    <div>
      <h1 className="text-2xl font-semibold text-vf-base">Compare candidates</h1>
      <p className="text-sm text-vf-muted mt-1">
        {count ? `${count} candidates` : "Select 2–5 candidates"}{positionName ? ` · ${positionName}` : ""}
      </p>
    </div>
    <div className="flex gap-2">
      {onEditSelection && (
        <button onClick={onEditSelection} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
          Edit selection
        </button>
      )}
      <button onClick={() => nav("/admin/interviews")} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
        ← Back to sessions
      </button>
    </div>
  </div>
);
